import { useCallback, useMemo } from 'react'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { ETH_ADDRESS } from '@components/Web3/utils'
import { balanceQueryKey, useBalance } from 'hooks/useBalance'
import { usdcBalanceQueryKey, useUSDCBalance } from 'hooks/useUsdcBalance'
import { queryClient } from 'use-towns-client'
import { Address } from 'viem'

export interface TreasuryCurrency {
    address: Address
    formatted: string
    value: bigint
    decimals: number
    hasBalance: boolean
    symbol: 'ETH' | 'USDC'
}

export interface TreasuryBalancesResult {
    currencies: TreasuryCurrency[]
    hasAnyBalance: boolean
    hasBothBalances: boolean
    isLoading: boolean
}

export function useTreasuryBalances(args: {
    spaceAddress: Address | undefined
    enabled?: boolean
    refetchInterval?: number
}): TreasuryBalancesResult {
    const { spaceAddress, enabled, refetchInterval } = args
    const { data: ethBalance, isLoading: isLoadingEth } = useBalance({
        address: spaceAddress,
        enabled: !!spaceAddress && enabled,
        refetchInterval: refetchInterval,
    })
    const { data: usdcBalance, isLoading: isLoadingUsdc } = useUSDCBalance({
        address: spaceAddress,
        enabled: !!spaceAddress && enabled,
        refetchInterval: refetchInterval,
    })
    const { address: usdcAddress } = useUSDCMetadata()

    return useMemo(() => {
        const currencies: TreasuryCurrency[] = []

        // ETH currency
        if (ethBalance) {
            currencies.push({
                address: ETH_ADDRESS,
                formatted: ethBalance.formatted,
                value: ethBalance.value,
                decimals: 18,
                hasBalance: ethBalance.value > 0n,
                symbol: 'ETH',
            })
        }

        // USDC currency
        if (usdcBalance) {
            currencies.push({
                address: usdcAddress,
                formatted: usdcBalance.formatted,
                value: usdcBalance.value,
                decimals: 6,
                hasBalance: usdcBalance.value > 0n,
                symbol: 'USDC',
            })
        }

        const availableCurrencies = currencies.filter((c) => c.hasBalance)

        return {
            currencies: availableCurrencies,
            hasAnyBalance: availableCurrencies.length > 0,
            hasBothBalances: availableCurrencies.length === 2,
            isLoading: isLoadingEth || isLoadingUsdc,
        }
    }, [ethBalance, usdcBalance, usdcAddress, isLoadingEth, isLoadingUsdc])
}

export const useRefetchTreasuryBalances = () => {
    const { address: usdcAddress } = useUSDCMetadata()
    return useCallback(
        async (spaceAddress: Address | undefined) => {
            if (!spaceAddress) {
                return
            }

            // Invalidate ETH balance
            await queryClient.refetchQueries({
                queryKey: balanceQueryKey(spaceAddress),
            })

            // Invalidate USDC balance
            await queryClient.refetchQueries({
                queryKey: usdcBalanceQueryKey(spaceAddress, usdcAddress),
            })
        },
        [usdcAddress],
    )
}
